import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Lock, CloudUpload, FileSearch, Check } from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";

const DOCTOR_IMG = "https://images.unsplash.com/photo-1638202993928-7267aad84c31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwcHJvZmVzc2lvbmFsJTIwY2xpbmljfGVufDB8fHx8MTc4NDA5OTIwNHww&ixlib=rb-4.1.0&q=85";

const benefits = [
  "Uniform medical standards", "Reduced medical disputes", "Lower repatriation risk",
  "Better medical governance", "Reduced operational delays", "Independent medical review",
  "Digital medical history",
];

const secureCards = [
  { icon: Lock, t: "Encrypted Storage", d: "End-to-end encrypted records on the MedVerify Server." },
  { icon: CloudUpload, t: "Google Cloud Backup", d: "Redundant encrypted backup for every reviewed PEME." },
  { icon: FileSearch, t: "Authorized Retrieval", d: "Instant access for future reference and audits." },
  { icon: ShieldCheck, t: "Audit-Ready Records", d: "Standardized digital documentation, always available." },
];

export default function PemeReview() {
  return (
    <main data-testid="peme-page" className="bg-[#f7f4ee] overflow-x-clip">
      {/* HERO */}
      <section className="relative pt-40 md:pt-52 pb-16 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#12294d] to-[#0c1d3a]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="overline-label mb-6">
            Service — Centralized Review
          </motion.p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.12] max-w-4xl" data-testid="peme-heading">
            <MaskedLines
              lines={[
                <>Centralized PEME Medical</>,
                <>Review & <span className="text-[#2ab5cf]">Verification</span></>,
              ]}
              startDelay={0.15}
            />
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8 text-lg md:text-xl text-slate-200 max-w-2xl leading-relaxed">
            Independent review of every seafarer medical examination by experienced maritime doctors —
            before fitness is finalized.
          </motion.p>
        </div>
      </section>

      {/* STANDARDIZING */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <h2 className="font-display text-[#12294d] text-3xl md:text-4xl tracking-tight leading-tight" data-testid="peme-standardizing-heading">
              Standardizing Maritime Medical Decisions
            </h2>
            <div className="mt-8 space-y-5 text-slate-600 leading-relaxed text-base md:text-lg">
              <p>
                Medical examinations performed at different clinics often vary in interpretation despite
                similar medical findings.
              </p>
              <p>
                MedVerify eliminates inconsistency by providing a centralized doctor review before medical
                fitness is finalized.
              </p>
              <p>
                Each case is independently reviewed by experienced maritime medical professionals using
                ILO Guidelines together with client-specific medical standards.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={DOCTOR_IMG} alt="Maritime medical professional" className="rounded-[2rem] w-full h-[420px] md:h-[460px] object-cover shadow-xl shadow-[#1a3a6b]/10 border border-white" />
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline-label">For Ship Management Companies</p>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight mt-4">Benefits</h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <StaggerItem key={i}>
                <div className="h-full flex items-start gap-3 bg-white border border-[#1a3a6b]/10 rounded-2xl p-6 shadow-lg shadow-[#1a3a6b]/5" data-testid={`peme-benefit-${i}`}>
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
                    <Check size={13} strokeWidth={3} className="text-[#2ab5cf]" />
                  </span>
                  <p className="text-sm font-semibold text-[#12294d]">{b}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SECURE DATA */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="overline-label">Secure Medical Data</p>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight mt-4" data-testid="peme-secure-heading">Data Security</h2>
            <p className="mt-8 text-slate-600 leading-relaxed text-base md:text-lg">
              Every reviewed PEME is securely stored on the MedVerify Server with encrypted Google Cloud
              backup, allowing authorized retrieval for future reference and audits.
            </p>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {secureCards.map((item, i) => (
              <StaggerItem key={i}>
                <div className="h-full bg-white border border-[#1a3a6b]/10 rounded-2xl p-7 shadow-lg shadow-[#1a3a6b]/5" data-testid={`peme-secure-card-${i}`}>
                  <item.icon size={22} className="text-[#2ab5cf]" />
                  <p className="mt-4 text-[#12294d] font-semibold text-sm">{item.t}</p>
                  <p className="mt-2 text-slate-500 text-xs leading-relaxed">{item.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2rem] p-10 md:p-14 bg-white border border-[#1a3a6b]/10 shadow-xl shadow-[#1a3a6b]/5 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <h2 className="font-display text-[#12294d] text-2xl md:text-3xl tracking-tight max-w-2xl">
                Explore our Clinic Quality Audit Program
              </h2>
              <Link to="/clinic-audit" data-testid="peme-cta-audit" className="btn-primary rounded-full px-8 py-4 text-sm font-bold inline-flex items-center gap-2 shrink-0">
                Clinic Quality Audit <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
